import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'

import { useAppDispatch } from 'common/hooks/redux'
import {
  hideRightPanel,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

type State = {
  selectedVariantCombinationId: string | undefined
  selectedVariantId: string | undefined
}

type VariantLanguageContextType = {
  state: State
  selectVariant: (value: { combinationId?: string; variantId: string }) => void
  clearVariant: () => void
}

type VariantLanguageContextReducerActions =
  | { type: 'selectVariant'; combinationId: string; variantId: string }
  | { type: 'clearVariant' }

const initialState: State = {
  selectedVariantCombinationId: undefined,
  selectedVariantId: undefined,
}

const variantLanguageContextReducer = (
  state: State,
  action: VariantLanguageContextReducerActions
) => {
  switch (action.type) {
    case 'selectVariant': {
      return {
        ...state,
        selectedVariantCombinationId: action.combinationId,
        selectedVariantId: action.variantId, // Assuming variantId is same as combinationId for now
      }
    }
    case 'clearVariant': {
      return initialState
    }
    default:
      return state
  }
}

const VariantLanguageContext = createContext<VariantLanguageContextType>({
  state: initialState,
  selectVariant: () => undefined,
  clearVariant: () => undefined,
})

const VariantLanguageContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const dispatch = useAppDispatch()
  const [state, dispatchVariantLanguageContextState] = useReducer(
    variantLanguageContextReducer,
    initialState
  )

  const selectVariant = useCallback(
    ({
      combinationId,
      variantId,
    }: {
      combinationId?: string
      variantId: string
    }) => {
      dispatchVariantLanguageContextState({
        type: 'selectVariant',
        combinationId: combinationId || '',
        variantId,
      })
      dispatch(showRightPanel())
    },
    [dispatch]
  )

  const clearVariant = useCallback(() => {
    dispatchVariantLanguageContextState({ type: 'clearVariant' })
    dispatch(hideRightPanel())
  }, [dispatch])

  const value = useMemo(
    () => ({ state, selectVariant, clearVariant }),
    [state, selectVariant, clearVariant]
  )

  return (
    <VariantLanguageContext.Provider value={value}>
      {children}
    </VariantLanguageContext.Provider>
  )
}

const useVariantLanguageContext = () => {
  const context = useContext(VariantLanguageContext)
  if (!context) {
    throw new Error(
      'useVariantLanguageContext must be used within VariantLanguageContextProvider'
    )
  }
  return context
}

export { VariantLanguageContextProvider, useVariantLanguageContext }
